/**
 * GoodGuardian — Client-side auth utilities
 * Token management + authenticated API helper
 */

// localStorage key name used to persist the JWT auth token across page loads.
// Not a secret — it's a storage bucket label, not a credential value.
const LOCAL_STORAGE_AUTH_KEY = 'cg_token'

export const getToken  = ()      => localStorage.getItem(LOCAL_STORAGE_AUTH_KEY)
export const setToken  = (token) => localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, token)
export const clearToken = ()     => localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY)
export const isLoggedIn = ()     => !!getToken()

// ── Authenticated fetch helper ─────────────────────────────────────────────────
export async function api(path, options = {}) {
  const token = getToken()
  const isFormData = options.body instanceof FormData

  const res = await fetch(path, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  })

  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new Error('UNAUTHORIZED')
  }

  return res
}

export async function apiJson(path, options = {}) {
  const res = await api(path, options)
  const data = await res.json()
  if (!res.ok) throw new Error(data?.error || `API error ${res.status}`)
  return data
}

// ── Auth actions ───────────────────────────────────────────────────────────────
export async function register({ email, password, name, state, situation }) {
  const data = await apiJson('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name, state, situation }),
  })
  setToken(data.token)
  return data.user
}

export async function login({ email, password }) {
  const data = await apiJson('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
  setToken(data.token)
  return data.user
}

export async function fetchMe() {
  const data = await apiJson('/api/auth/me')
  return data.user
}

export async function updateProfile({ name, state, situation }) {
  const data = await apiJson('/api/auth/me', {
    method: 'PUT',
    body: JSON.stringify({ name, state, situation }),
  })
  return data.user
}

export function logout() {
  clearToken()
  window.location.href = '/login'
}

// ── Data sync helpers ──────────────────────────────────────────────────────────
export const data = {
  // Events
  getEvents:   ()          => apiJson('/api/data/events'),
  postEvent:   (body)      => apiJson('/api/data/events', { method: 'POST', body: JSON.stringify(body) }),
  putEvent:    (id, body)  => apiJson(`/api/data/events?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteEvent: (id)        => apiJson(`/api/data/events?id=${id}`, { method: 'DELETE' }),

  // Comms
  getComms:   ()         => apiJson('/api/data/comms'),
  postComm:   (body)     => apiJson('/api/data/comms', { method: 'POST', body: JSON.stringify(body) }),
  putComm:    (id, body) => apiJson(`/api/data/comms?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteComm: (id)       => apiJson(`/api/data/comms?id=${id}`, { method: 'DELETE' }),

  // Documents
  getDocs:    ()         => apiJson('/api/data/documents'),
  getDoc:     (id)       => apiJson(`/api/data/documents?id=${id}`),
  postDoc:    (body)     => apiJson('/api/data/documents', { method: 'POST', body: JSON.stringify(body) }),
  putDoc:     (id, body) => apiJson(`/api/data/documents?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteDoc:  (id)       => apiJson(`/api/data/documents?id=${id}`, { method: 'DELETE' }),

  // Chats
  getChats:   (agent = 'chat') => apiJson(`/api/data/chats?agent=${agent}`),
  postChat:   (body)           => apiJson('/api/data/chats', { method: 'POST', body: JSON.stringify(body) }),
  clearChat:  (agent)          => apiJson(`/api/data/chats?agent=${agent}`, { method: 'DELETE' }),

  // Files
  getFiles:   ()             => apiJson('/api/data/files'),
  getFile:    (id)           => api(`/api/data/files?id=${id}`),   // returns Response (binary)
  renameFile: (id, filename) => apiJson(`/api/data/files?id=${id}`, { method: 'PUT', body: JSON.stringify({ filename }) }),
  deleteFile: (id)           => apiJson(`/api/data/files?id=${id}`, { method: 'DELETE' }),
  uploadFile: (file) => {
    const form = new FormData()
    form.append('file', file)
    return apiJson('/api/data/files', { method: 'POST', body: form })
  },

  // Co-parent invites
  getInvites:      ()      => apiJson('/api/social/invite'),
  sendInvite:      (email)          => apiJson('/api/social/invite', { method: 'POST', body: JSON.stringify({ invite_email: email }) }),
  resendInvite:    (email)          => apiJson('/api/social/invite', { method: 'POST', body: JSON.stringify({ invite_email: email, resend: true }) }),
  revokeInvite:    (id)    => apiJson(`/api/social/invite?id=${id}`, { method: 'DELETE' }),
  acceptInvite:    (token) => apiJson('/api/social/accept', { method: 'POST', body: JSON.stringify({ token }) }),
  generateQRInvite: ()     => apiJson('/api/social/invite-qr', { method: 'POST' }),

  // Children
  getChildren:   ()        => apiJson('/api/data/children'),
  getChild:      (id)      => apiJson(`/api/data/children?id=${id}`),
  postChild:     (body)    => apiJson('/api/data/children', { method: 'POST', body: JSON.stringify(body) }),
  putChild:      (id, body)=> apiJson(`/api/data/children?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteChild:   (id)      => apiJson(`/api/data/children?id=${id}`, { method: 'DELETE' }),

  // Calendar
  getCalendar:      ()           => apiJson('/api/data/calendar'),
  postCalEvent:     (body)       => apiJson('/api/data/calendar', { method: 'POST', body: JSON.stringify(body) }),
  putCalEvent:      (id, body)   => apiJson(`/api/data/calendar?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteCalEvent:   (id)         => apiJson(`/api/data/calendar?id=${id}`, { method: 'DELETE' }),

  // Calendar change requests
  getCalRequests:   ()           => apiJson('/api/data/calendar-requests'),
  postCalRequest:   (body)       => apiJson('/api/data/calendar-requests', { method: 'POST', body: JSON.stringify(body) }),
  putCalRequest:    (id, body)   => apiJson(`/api/data/calendar-requests?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteCalRequest: (id)         => apiJson(`/api/data/calendar-requests?id=${id}`, { method: 'DELETE' }),

  // Court orders
  getOrders:     ()        => apiJson('/api/data/orders'),
  postOrder:     (body)    => apiJson('/api/data/orders', { method: 'POST', body: JSON.stringify(body) }),
  putOrder:      (id, body)=> apiJson(`/api/data/orders?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOrder:   (id)      => apiJson(`/api/data/orders?id=${id}`, { method: 'DELETE' }),
  scanOrder:     (file)    => {
    const form = new FormData()
    form.append('file', file)
    return api('/api/data/orders-scan', { method: 'POST', body: form }).then(async res => {
      const json = await res.json()
      if (!res.ok) throw Object.assign(new Error(json?.message || `Scan error ${res.status}`), json)
      return json
    })
  },

  // Medical records
  getMedical:    (childId) => apiJson(`/api/data/medical?child_id=${childId}`),
  getMedRecord:  (id)      => apiJson(`/api/data/medical?id=${id}`),
  postMedRecord: (body)    => apiJson('/api/data/medical', { method: 'POST', body: JSON.stringify(body) }),
  putMedRecord:  (id, body)=> apiJson(`/api/data/medical?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteMedRecord:(id)     => apiJson(`/api/data/medical?id=${id}`, { method: 'DELETE' }),

  // Notifications
  getNotifications:  ()   => apiJson('/api/data/notifications'),
  markNotifRead:     (id) => apiJson(id ? `/api/data/notifications?id=${id}` : '/api/data/notifications', { method: 'PUT' }),

  // Messages (in-app inbox)
  getMessages:       ()   => apiJson('/api/data/messages'),
  markMsgRead:       (id) => apiJson(id ? `/api/data/messages?id=${id}` : '/api/data/messages', { method: 'PUT' }),
  deleteMessage:     (id) => apiJson(`/api/data/messages?id=${id}`, { method: 'DELETE' }),

  // Settings
  getSettings:       ()     => apiJson('/api/user/settings'),
  putSettings:       (body) => apiJson('/api/user/settings', { method: 'PUT', body: JSON.stringify(body) }),

  // Direct Messaging (Private Communications Hub)
  getEncryptionStatus: ()        => apiJson('/api/messaging/encryption-status'),
  getConversations:  ()          => apiJson('/api/messaging/conversations'),
  postConversation:  (body)      => apiJson('/api/messaging/conversations', { method: 'POST', body: JSON.stringify(body) }),
  deleteConversation:(id)        => apiJson(`/api/messaging/conversations?id=${id}`, { method: 'DELETE' }),
  getConvMessages:   (convId)    => apiJson(`/api/messaging/messages?conv_id=${convId}`),
  postConvMessage:   (convId, text) => apiJson('/api/messaging/messages', { method: 'POST', body: JSON.stringify({ conv_id: convId, text }) }),
  markConvRead:      (convId)    => apiJson(`/api/messaging/messages?conv_id=${convId}`, { method: 'PUT' }),
  exportConversation:(convId)    => `/api/messaging/export?conv_id=${convId}`,
  analyzeTone:       (message)   => apiJson('/api/tone-analyzer', { method: 'POST', body: JSON.stringify({ message }) }),

  // Violations
  getViolations:   ()          => apiJson('/api/data/violations'),
  postViolation:   (body)      => apiJson('/api/data/violations', { method: 'POST', body: JSON.stringify(body) }),
  putViolation:    (id, body)  => apiJson(`/api/data/violations?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteViolation: (id)        => apiJson(`/api/data/violations?id=${id}`, { method: 'DELETE' }),

  // Wellbeing
  getWellbeing:    (childId) => apiJson(childId ? `/api/data/wellbeing?child_id=${childId}` : '/api/data/wellbeing'),
  postWellbeing:   (body)    => apiJson('/api/data/wellbeing', { method: 'POST', body: JSON.stringify(body) }),
  putWellbeing:    (id, body) => apiJson(`/api/data/wellbeing?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteWellbeing: (id)      => apiJson(`/api/data/wellbeing?id=${id}`, { method: 'DELETE' }),

  // Parenting Time
  getParentingTime:      ()          => apiJson('/api/data/parenting-time'),
  addParentingPeriod:    (body)      => apiJson('/api/data/parenting-time', { method: 'POST', body: JSON.stringify(body) }),
  updateParentingPeriod: (id, body)  => apiJson(`/api/data/parenting-time?id=${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteParentingPeriod: (id)        => apiJson(`/api/data/parenting-time?id=${id}`, { method: 'DELETE' }),

  // Platform import (OFW, TalkingParents, etc.)
  importPlatform: (file) => {
    const form = new FormData()
    form.append('file', file)
    return api('/api/import-platform', { method: 'POST', body: form }).then(async res => {
      const json = await res.json()
      if (!res.ok) throw new Error(json?.error || `Import error ${res.status}`)
      return json
    })
  },

  // Expenses
  getExpenses:       (status)    => apiJson(status ? `/api/expenses/expenses?status=${status}` : '/api/expenses/expenses'),
  postExpense:       (body)      => apiJson('/api/expenses/expenses', { method: 'POST', body: JSON.stringify(body) }),
  actionExpense:     (id, action, payment_method, note) =>
    apiJson(`/api/expenses/expenses?id=${id}`, { method: 'PUT', body: JSON.stringify({ action, payment_method, note }) }),
  deleteExpense:     (id)        => apiJson(`/api/expenses/expenses?id=${id}`, { method: 'DELETE' }),
}
